import type { QueryField } from "./types";

export type FieldGroupSpans = {
  /** Maps the head row id of each same-name group to its row count.
   *  Only the Name column is spanned; all other columns render
   *  per-variant values independently. */
  name: Map<string, number>;
  /** Row ids covered by a name span (not the head). AG Grid still renders
   *  these cells underneath the spanning cell — callers should hide them. */
  spannedOver: Set<string>;
};

/** Gather same-name fields into one contiguous run, each group sitting where
 * its first member fell. Variants of a field are added at different times, so
 * the persisted sort order scatters them; grouping is what puts `Balance (3)`
 * next to `Balance (1)` instead of further down the list. */
export const groupFieldsByName = (fields: ReadonlyArray<QueryField>): QueryField[] => {
  const groups = new Map<string, QueryField[]>();
  for (const field of fields) {
    const existing = groups.get(field.name);
    if (existing) existing.push(field);
    else groups.set(field.name, [field]);
  }
  return Array.from(groups.values()).flat();
};

export type NameCellSpanRole = "spannedOver" | "spanHead" | "single";

export const getNameCellSpanRole = (
  spans: FieldGroupSpans,
  rowId: string,
): NameCellSpanRole => {
  if (spans.spannedOver.has(rowId)) return "spannedOver";
  if ((spans.name.get(rowId) ?? 1) > 1) return "spanHead";
  return "single";
};

const emptySpans = (): FieldGroupSpans => ({ name: new Map(), spannedOver: new Set() });

/** Precompute name-column row spans for fields that share a name.
 *  Only consecutive same-name rows are counted (AG Grid rowSpan only
 *  works within a contiguous run). Sorting collapses all spans to 1. */
export const computeFieldGroupSpans = (
  rowData: readonly QueryField[],
  isSorted: boolean,
): FieldGroupSpans => {
  if (isSorted || rowData.length === 0) {
    return emptySpans();
  }

  const spans = emptySpans();
  let index = 0;
  while (index < rowData.length) {
    let count = 1;
    while (
      index + count < rowData.length &&
      rowData[index + count].name === rowData[index].name
    ) {
      count += 1;
    }
    const head = rowData[index];
    if (head) {
      spans.name.set(head.id, count);
      if (count > 1) {
        for (let offset = 1; offset < count; offset += 1) {
          const covered = rowData[index + offset];
          if (covered) spans.spannedOver.add(covered.id);
        }
      }
    }
    index += count;
  }

  return spans;
};
